import { useEffect, useRef } from "react";
import {
  useCompanyFiltersStore,
  useCompanyFilterOptionsMap,
  useCompanyFiltersStoreApi,
} from "./provider/CompanyFilterProvider";
import { useShallow } from "zustand/shallow";
import {
  selectAppliedFilters,
  selectPersistedFilters,
  selectHasUnsavedChanges,
  selectHasPendingChanges,
} from "./store/selectors";
import type { ModuleKey } from "./modules";

// =============================================================================
// useCompanyFilters - Access filter state values
// =============================================================================

export function useCompanyFilters() {
  return useCompanyFiltersStore(
    useShallow((s) => ({
      draftFilters: s.draftFilters,
      appliedFilters: selectAppliedFilters(s),
      persistedFilters: selectPersistedFilters(s),
      hasUnsavedChanges: selectHasUnsavedChanges(s),
      hasPendingChanges: selectHasPendingChanges(s),
    }))
  );
}

// =============================================================================
// useCompanyFilterActions - Access store actions
// =============================================================================

// Select actions in one go (shallow) to avoid repeated subscriptions.
export function useCompanyFilterActions() {
  return useCompanyFiltersStore(
    useShallow((s) => ({
      setSlice: s.setSlice,
      resetAll: s.resetAll,
      applyDraft: s.applyDraft,
      revertDraftToApplied: s.revertDraftToApplied,
      hydrateFromCompanyFilters: s.hydrateFromCompanyFilters,
      serializeAppliedToJson: s.serializeAppliedToJson,
      markAsPersisted: s.markAsPersisted,
      clearDraft: s.clearDraft,
      clearAll: s.clearAll,
    }))
  );
}

// =============================================================================
// useCompanyFilterSlice - Access a specific module's slice
// =============================================================================

export function useCompanyFilterSlice<K extends ModuleKey>(key: K) {
  return useCompanyFiltersStore((s) => s.slices[key]);
}

// =============================================================================
// useCompanyFilterOptions - Access module-specific options
// =============================================================================

export function useCompanyFilterOptions<K extends ModuleKey>(key: K) {
  const options = useCompanyFilterOptionsMap();
  return options?.[key];
}

// =============================================================================
// useDebouncedAutoApply - Auto-apply draft filters after debounce
// =============================================================================

const DEBOUNCE_MS = 300;

export function useDebouncedAutoApply(enabled: boolean = true): void {
  const store = useCompanyFiltersStoreApi();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = store.subscribe(
      (s) => s.draftFilters,
      () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          store.getState()._applyDraftToApplied();
        }, DEBOUNCE_MS);
      }
    );

    return () => {
      unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, store]);
}
